/**
 * The words and cuts behind the assistant's history: which messages a turn
 * posts, how the list groups its rows, and what a restored card is told.
 * docs/plans/agent-thread-history.md. Pure, so every rule is proven without a
 * device.
 */
import type { AgentProposalOutcome, AgentThreadSummary } from '@alluzo/shared';
import { formatStamp } from '@alluzo/shared';
import type { UIMessage } from 'ai';

import { t } from '@/lib/i18n';

/** The server's ceiling on one turn's messages. Kept in step with MAX_MESSAGES in routes/agent.ts. */
export const SEND_WINDOW = 40;

/**
 * The trailing window of a long thread that one turn posts (decision 7). The
 * server keeps the whole thread and upserts by message id, so an older
 * message left out of the window is untouched there. The cut starts on a user
 * message, because a history that opens on a reply reads to the model like a
 * conversation with the first question missing.
 */
export const sendWindow = (messages: readonly UIMessage[], max = SEND_WINDOW): UIMessage[] => {
  if (messages.length <= max) return [...messages];
  let start = messages.length - max;
  while (start < messages.length - 1 && messages[start]?.role !== 'user') start += 1;
  return messages.slice(start);
};

/**
 * The thread with a call's turn put in: each message it does not hold yet,
 * once, after the rest — the same array when nothing is new. While a typed
 * turn is in flight, a reply at the end is the one it streams into, and the
 * AI SDK writes a streaming reply in place only while it is the thread's last
 * message (it pushes it again otherwise, and the reply is drawn twice), so the
 * call's turn goes in before that reply.
 */
export const appendedTo = <M extends { readonly id: string; readonly role: string }>(
  held: M[],
  incoming: readonly M[],
  typedInFlight: boolean,
): M[] => {
  const have = new Set(held.map((message) => message.id));
  const fresh = incoming.filter((message) => !have.has(message.id));
  if (fresh.length === 0) return held;
  const last = held[held.length - 1];
  if (typedInFlight && last?.role === 'assistant') return [...held.slice(0, -1), ...fresh, last];
  return [...held, ...fresh];
};

/* -------------------------------------------------------------- the list */

export type HistorySectionKey = 'today' | 'yesterday' | 'week' | 'earlier';

export type HistorySection = {
  readonly key: HistorySectionKey;
  readonly title: string;
  readonly threads: readonly AgentThreadSummary[];
};

const ORDER: readonly HistorySectionKey[] = ['today', 'yesterday', 'week', 'earlier'];

const startOfDay = (instant: Date): number => {
  const day = new Date(instant);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
};

/** Which section a thread's last message puts it in, by the device's local day. */
export const sectionOf = (lastMessageAt: string, now: Date): HistorySectionKey => {
  const today = startOfDay(now);
  const at = new Date(lastMessageAt).getTime();
  if (Number.isNaN(at)) return 'earlier';
  const dayMs = 24 * 60 * 60 * 1000;
  if (at >= today) return 'today';
  if (at >= today - dayMs) return 'yesterday';
  if (at >= today - 6 * dayMs) return 'week';
  return 'earlier';
};

/**
 * The list, grouped and titled, in the order the rows arrived (newest first).
 * A section with nothing in it is not drawn.
 */
export const historySections = (threads: readonly AgentThreadSummary[], now: Date): HistorySection[] => {
  const buckets = new Map<HistorySectionKey, AgentThreadSummary[]>();
  for (const thread of threads) {
    const key = sectionOf(thread.lastMessageAt, now);
    const bucket = buckets.get(key);
    if (bucket === undefined) buckets.set(key, [thread]);
    else bucket.push(thread);
  }
  return ORDER.filter((key) => buckets.has(key)).map((key) => ({
    key,
    title: t(`agent.history.section.${key}`),
    threads: buckets.get(key) ?? [],
  }));
};

/** The time a row shows: a clock today, a day otherwise. */
export const historyStamp = (lastMessageAt: string, now: Date): string =>
  formatStamp(lastMessageAt, now);

/** "In Sales · 3:40 pm". The place is the record when known, else the business. */
export const historyMeta = (where: string | null, lastMessageAt: string, now: Date): string => {
  const stamp = historyStamp(lastMessageAt, now);
  if (where === null) return stamp;
  return stamp === '' ? t('agent.here', { place: where }) : `${t('agent.here', { place: where })} · ${stamp}`;
};

/* ----------------------------------------------------------- the outcomes */

/** The outcomes of a thread, by the call that drew each card. */
export const outcomesByCall = (outcomes: readonly AgentProposalOutcome[]): ReadonlyMap<string, AgentProposalOutcome> =>
  new Map(outcomes.map((outcome) => [outcome.toolCallId, outcome]));
